/**
 * Schreibwege wiederkehrender Aufgaben (F-24). Die Regeln stehen in
 * `domain/tasks/wiederholung.ts`; hier wird nur geschrieben, jeweils in einer
 * Transaktion, damit Serie und Kopie nie auseinanderlaufen.
 *
 * Ein Zeitblock im Kalender gehoert zum Vorkommen: Beim Weiterruecken nimmt
 * die erledigte Kopie den Block mit (fuer die Zeitauswertung, F-28), und die
 * Serie bekommt einen neuen Block zur gleichen Uhrzeit am neuen Tag.
 */
import { db } from '@/db/schema';
import { abhakPlan, datenFuer, nachDemAktuellen, wiederholungAnlegen } from '@/domain/tasks/wiederholung';
import { inDenPapierkorb, rueckgaengigMachbar } from '@/db/repositories/papierkorb';
import { differenceInDays, formatDate, nowInstant, today } from '@/lib/datetime';
import { shiftFloating } from '@/lib/recurrence/edit';
import { newId } from '@/lib/utils/id';
import type { ID, LocalDate, RecurrenceEditScope, Task } from '@/domain/types';

const TAG_MS = 86_400_000;

/** Kopie eines Vorkommens: ohne Regel, mit Verweis auf die Serie. */
function kopie(t: Task, vorkommen: LocalDate, patch: Partial<Task>): Task {
  const k: Task = { ...t, ...datenFuer(t, vorkommen), ...patch, id: newId(), serie: { id: t.id, vorkommen }, createdAt: nowInstant(), updatedAt: nowInstant() };
  delete k.wiederholung;
  if (!k.plannedDate) delete k.plannedDate;
  if (!k.dueDate) delete k.dueDate;
  return k;
}

/**
 * Die Serie auf `naechstes` weiterruecken. Ein vorhandener Zeitblock geht an
 * `kopieId` (oder bleibt ohne Aufgabe stehen); die Serie bekommt einen neuen.
 */
async function weiterruecken(t: Task, naechstes: LocalDate, kopieId: ID | null): Promise<void> {
  const ts = nowInstant();
  const daten = datenFuer(t, naechstes);
  let timeBlockEventId: ID | undefined;
  const block = t.timeBlockEventId ? await db.events.get(t.timeBlockEventId) : undefined;
  if (block) {
    if (kopieId) await db.events.update(block.id, { sourceTaskId: kopieId, updatedAt: ts });
    else await db.events.delete(block.id);
    if (daten.plannedDate && t.plannedDate) {
      const ms = differenceInDays(t.plannedDate, daten.plannedDate) * TAG_MS;
      timeBlockEventId = newId();
      await db.events.add({
        ...block, id: timeBlockEventId, sourceTaskId: t.id,
        start: shiftFloating(String(block.start), ms), end: shiftFloating(String(block.end), ms),
        createdAt: ts, updatedAt: ts,
      });
    }
  }
  const neu: Task = {
    ...t, ...daten, isBigThree: false, updatedAt: ts,
    wiederholung: { ...t.wiederholung!, vorkommen: naechstes },
    status: t.status === 'erledigt' ? 'naechste' : t.status,
  };
  if (timeBlockEventId) neu.timeBlockEventId = timeBlockEventId;
  else delete neu.timeBlockEventId;
  if (!neu.plannedDate) delete neu.plannedDate;
  if (!neu.dueDate) delete neu.dueDate;
  await db.tasks.put(neu);
}

/**
 * Wiederholung setzen oder entfernen (Aufgaben-Dialog). Anker ist der
 * Plantag, sonst die Deadline, sonst heute. Ohne beides wird das erste
 * Vorkommen eingeplant - sonst erschiene die Aufgabe an keinem Tag.
 */
export async function wiederholungSetzen(taskId: ID, rrule: string | null): Promise<boolean> {
  return db.transaction('rw', db.tasks, async () => {
    const t = await db.tasks.get(taskId);
    if (!t) return false;
    if (!rrule) {
      if (!t.wiederholung) return true;
      const rest = { ...t, updatedAt: nowInstant() };
      delete rest.wiederholung;
      await db.tasks.put(rest);
      return true;
    }
    const anker = t.plannedDate ?? t.dueDate ?? today();
    const w = wiederholungAnlegen(rrule, anker);
    if (!w) return false;
    const daten = datenFuer({ ...t, wiederholung: { ...w, vorkommen: anker } }, w.vorkommen);
    const neu: Task = { ...t, ...daten, wiederholung: w, updatedAt: nowInstant() };
    if (!neu.plannedDate && !neu.dueDate) neu.plannedDate = w.vorkommen;
    if (!neu.dueDate) delete neu.dueDate;
    await db.tasks.put(neu);
    return true;
  });
}

/**
 * Ein Vorkommen abhaken: erledigte Kopie anlegen, Serie weiterruecken. Am
 * Ende der Serie wird die Aufgabe selbst erledigt.
 */
export async function serieAbhaken(taskId: ID, heute: LocalDate = today()): Promise<ID | null> {
  // Mit Rueckgaengig-Meldung (F-45): Kopie weg, Serie und Zeitblock zurueck.
  return rueckgaengigMachbar({ art: 'erledigt', tabelle: 'tasks', zeileIds: [taskId] }, () => db.transaction('rw', db.tasks, db.events, async () => {
    const t = await db.tasks.get(taskId);
    if (!t?.wiederholung || t.status === 'erledigt') return null;
    const plan = abhakPlan(t.wiederholung, heute);
    if (!plan.naechstes) {
      await db.tasks.update(t.id, { ...datenFuer(t, plan.erledigt), status: 'erledigt', completedAt: nowInstant(), updatedAt: nowInstant() });
      return t.id;
    }
    const k = kopie(t, plan.erledigt, { status: 'erledigt', completedAt: nowInstant() });
    await db.tasks.add(k);
    await weiterruecken(t, plan.naechstes, k.id);
    return k.id;
  }), [db.tasks, db.events]);
}

/**
 * "Nur dieses" bearbeiten: das aktuelle Vorkommen wird eine eigene Aufgabe
 * (mit Verweis auf die Serie), die Serie rueckt weiter. Gibt die ID der
 * Kopie zurueck - auf sie werden die Aenderungen angewendet.
 */
export async function vorkommenAbspalten(taskId: ID): Promise<ID | null> {
  return db.transaction('rw', db.tasks, db.events, async () => {
    const t = await db.tasks.get(taskId);
    if (!t?.wiederholung) return null;
    const naechstes = nachDemAktuellen(t.wiederholung);
    if (!naechstes) {
      const rest = { ...t, updatedAt: nowInstant() };
      delete rest.wiederholung;
      await db.tasks.put(rest);
      return t.id;
    }
    const k = kopie(t, t.wiederholung.vorkommen, { isBigThree: t.isBigThree });
    await db.tasks.add(k);
    await weiterruecken(t, naechstes, k.id);
    return k.id;
  });
}

/** Alle Vorkommen (Kopien) einer Serie. */
export function vorkommenDerSerie(serieId: ID): Promise<Task[]> {
  return db.tasks.filter((t) => t.serie?.id === serieId).toArray();
}

/** "Alle": Titel, Notizen und Zuordnung auch an den bisherigen Vorkommen. */
export async function aufSerieUebertragen(serieId: ID): Promise<void> {
  await db.transaction('rw', db.tasks, async () => {
    const t = await db.tasks.get(serieId);
    if (!t) return;
    const ts = nowInstant();
    await db.tasks.filter((x) => x.serie?.id === serieId).modify((x) => {
      x.title = t.title;
      x.areaId = t.areaId;
      if (t.notes) x.notes = t.notes; else delete x.notes;
      if (t.projectId) x.projectId = t.projectId; else delete x.projectId;
      if (t.goalId) x.goalId = t.goalId; else delete x.goalId;
      x.updatedAt = ts;
    });
  });
}

/**
 * Loeschen mit Rueckfrage (F-24) - jeweils als EIN Papierkorb-Eintrag (F-45):
 *  - nur dieses: das aktuelle Vorkommen ueberspringen
 *  - dieses und folgende: die Serie endet, Erledigtes bleibt
 *  - alle: Serie und alle bisherigen Vorkommen
 */
export async function serieLoeschen(taskId: ID, scope: RecurrenceEditScope): Promise<void> {
  const vorher = await db.tasks.get(taskId);
  if (!vorher) return;
  const titel = scope === 'dieser' && vorher.wiederholung ? `${vorher.title} am ${formatDate(vorher.wiederholung.vorkommen)}` : undefined;
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'tasks', zeileIds: [taskId], titel }, () => db.transaction('rw', db.tasks, db.events, async () => {
    const t = await db.tasks.get(taskId);
    if (!t) return;
    if (scope === 'dieser' && t.wiederholung) {
      const naechstes = nachDemAktuellen(t.wiederholung);
      if (naechstes) {
        await weiterruecken(t, naechstes, null);
        return;
      }
    }
    const ids = [t.id, ...(scope === 'alle' ? (await vorkommenDerSerie(t.id)).map((x) => x.id) : [])];
    for (const id of ids) {
      await db.events.where('sourceTaskId').equals(id).modify((e) => { delete e.sourceTaskId; });
      await db.events.where('taskIds').equals(id).modify((e) => {
        const rest = (e.taskIds ?? []).filter((x) => x !== id);
        if (rest.length) e.taskIds = rest; else delete e.taskIds;
      });
    }
    await db.tasks.bulkDelete(ids);
  }));
}
